import { useState } from 'react';
import { paginasVisibles } from '@lib/utils/paginacion';

/** A partir de cuántas páginas aparece el campo para saltar a una puntual. */
const PAGINAS_PARA_SALTO = 10;

/**
 * Paginador con números, para poder ir a una página puntual y no solo de a una
 * con Anterior/Siguiente. Cuando hay muchas páginas la lista se resume con "…"
 * y aparece un campo para saltar directo, que es el único modo de llegar a las
 * que el resumen esconde.
 *
 * Nació en el Reporte ABM Web; vive acá para que el Reporte de padrón (VEN-512)
 * pagine igual sin copiarlo.
 */
export function Paginador({
  page,
  totalPaginas,
  disabled,
  onChange,
}: {
  page: number;
  totalPaginas: number;
  disabled?: boolean;
  onChange: (p: number) => void;
}) {
  const [salto, setSalto] = useState('');

  function irASalto(e: React.FormEvent) {
    e.preventDefault();
    const n = Number(salto);
    if (!Number.isInteger(n) || n < 1 || n > totalPaginas) return;
    onChange(n);
    setSalto('');
  }

  return (
    <nav
      className="rowBetween"
      style={{ alignItems: 'center', marginTop: 12, gap: 12, flexWrap: 'wrap' }}
      aria-label="Paginación del reporte"
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="btn btn-ghost btn--sm"
          disabled={page <= 1 || disabled}
          onClick={() => onChange(Math.max(1, page - 1))}
        >
          Anterior
        </button>

        {paginasVisibles(page, totalPaginas).map((p, i) =>
          p === 'gap' ? (
            <span
              // Los "…" no tienen id propio; el índice alcanza porque la lista
              // se regenera entera en cada render.
              key={`gap-${i}`}
              className="text-element-mid"
              style={{ padding: '0 4px' }}
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <button
              key={p}
              className={`btn btn--sm ${p === page ? 'btn-primary' : 'btn-ghost'}`}
              style={{ minWidth: 40 }}
              disabled={disabled}
              aria-current={p === page ? 'page' : undefined}
              aria-label={`Ir a la página ${p}`}
              onClick={() => onChange(p)}
            >
              {p}
            </button>
          ),
        )}

        <button
          className="btn btn-ghost btn--sm"
          disabled={page >= totalPaginas || disabled}
          onClick={() => onChange(Math.min(totalPaginas, page + 1))}
        >
          Siguiente
        </button>
      </div>

      {totalPaginas >= PAGINAS_PARA_SALTO && (
        <form onSubmit={irASalto} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <label className="text-element-mid" style={{ fontSize: 13 }} htmlFor="ir-a-pagina">
            Ir a
          </label>
          <input
            id="ir-a-pagina"
            type="number"
            min={1}
            max={totalPaginas}
            value={salto}
            onChange={(e) => setSalto(e.target.value)}
            placeholder={String(page)}
            style={{ width: 72 }}
            aria-label={`Ir a una página entre 1 y ${totalPaginas}`}
          />
          <button className="btn btn-ghost btn--sm" type="submit" disabled={disabled || !salto}>
            Ir
          </button>
        </form>
      )}
    </nav>
  );
}
